import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { PILLARS, COUNTERS } from "@/data/site";
import { SectionHeading, Marquee } from "@/components/pp/Shared";
import { fadeUp, stagger } from "@/lib/motion";

function Counter({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const dur = 1600, start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return <span ref={ref}>{n.toLocaleString("en-IN")}{suffix}</span>;
}

export default function WhoWeAre() {
  return (
    <section id="about" className="relative py-24 md:py-32" data-testid="about">
      <div className="pp-container">
        <SectionHeading
          overline="Who We Are"
          title={<>Not Just an Agency.<br />Your Creative Growth Engine.</>}
          sub="PixelPapad brings together visual storytellers, strategists, designers, editors, marketers and production specialists. We transform raw ideas into distinctive brand experiences designed to capture attention and support measurable business growth."
          testid="about-heading"
        />

        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map((p, i) => (
            <motion.div key={p.title} variants={fadeUp} custom={i}
              whileHover={{ y: -8, rotateX: 6, rotateY: -6 }}
              style={{ transformStyle: "preserve-3d", perspective: 800 }}
              className="pp-glass rounded-xl p-7 min-h-[190px] flex flex-col justify-between"
              data-testid={`pillar-${p.title.toLowerCase()}`}
            >
              <div className="h-10 w-10 rounded-lg grid place-items-center font-display text-lg text-black"
                style={{ background: p.color }}>{String(i + 1).padStart(2, "0")}</div>
              <div>
                <h3 className="font-display uppercase text-2xl tracking-wide text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {COUNTERS.map((c) => (
            <div key={c.label} className="text-center lg:text-left" data-testid={`counter-${c.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="font-display text-5xl md:text-6xl pp-text-gradient leading-none">
                <Counter value={c.value} suffix={c.suffix} />
              </div>
              <p className="mt-3 text-sm text-slate-400">{c.label}</p>
              {c.placeholder && <p className="text-[10px] uppercase tracking-widest text-slate-600 mt-1">placeholder data</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-20">
        <Marquee />
      </div>
    </section>
  );
}
